import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { getDeviceId } from "./device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// ----- Resilient token storage (fallback for browsers that drop SameSite=None cookies) -----
const TOKEN_KEY = "sr_session_token";

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (e) {
    console.warn("[auth] localStorage read failed (private mode?)", e?.message);
    return null;
  }
}

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    console.warn("[auth] localStorage write failed (private mode?)", e?.message);
  }
  applyAuthHeader(token);
}

export function applyAuthHeader(token) {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
}

// Restore header on module load so the first /me call carries it
applyAuthHeader(getStoredToken());

const AuthCtx = createContext({
  loading: true,
  authenticated: false,
  user: null,
  history: [],
  subscription: { is_premium: false, plan: "free", status: "none", cancel_at_period_end: false },
  quota: { cv_remaining: 0, cover_letter_remaining: 0, unlimited: false, daily_limit_cv: 1, daily_limit_cover_letter: 1 },
  pricing: { amount: 2.99, currency: "eur", interval: "month" },
  refresh: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    authenticated: false,
    user: null,
    history: [],
    subscription: { is_premium: false, plan: "free", status: "none", cancel_at_period_end: false },
    quota: { cv_remaining: 0, cover_letter_remaining: 0, unlimited: false, daily_limit_cv: 1, daily_limit_cover_letter: 1 },
    pricing: { amount: 2.99, currency: "eur", interval: "month" },
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me`, {
        params: { device_id: getDeviceId() },
      });
      setState({
        loading: false,
        authenticated: !!data.authenticated,
        user: data.user || null,
        history: data.cv_history || [],
        subscription: data.subscription || { is_premium: false, plan: "free", status: "none", cancel_at_period_end: false },
        quota: data.quota || { cv_remaining: 0, cover_letter_remaining: 0, unlimited: false, daily_limit_cv: 1, daily_limit_cover_letter: 1 },
        pricing: data.pricing || { amount: 2.99, currency: "eur", interval: "month" },
      });
      // If we were authenticated but the stored token is stale (server says no),
      // clear it to avoid sending a dead Bearer header on every call.
      if (!data.authenticated && getStoredToken()) {
        setStoredToken(null);
      }
    } catch (err) {
      // Log so prod issues are diagnosable.
      console.warn("[auth] /api/me failed", err?.response?.status, err?.message);
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (e) {
      // Best-effort: even if the server call fails (network, 401, etc.),
      // we still clear local state below.
      console.warn("[auth] logout call failed", e?.response?.status, e?.message);
    }
    setStoredToken(null);
    await refresh();
  }, [refresh]);

  /* eslint-disable */ /* Intentional: this effect runs once on mount to
     either skip the /me check (OAuth callback path) or trigger refresh()
     which manages auth state. */
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    refresh();
  }, [refresh]);
  /* eslint-enable */

  return (
    <AuthCtx.Provider value={{ ...state, refresh, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useSession() {
  return useContext(AuthCtx);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startGoogleLogin() {
  const redirectUrl = `${window.location.origin}/auth/callback`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
